import { useState } from "react";
import { motion } from "framer-motion";
import { Copy, ShieldCheck, MailOpen, Star, Landmark, Lock } from "lucide-react";
import { toast } from "sonner";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import StripeBar from "@/components/StripeBar";
import { postForm } from "@/lib/api";
import { CHECK_INFO, DISCLAIMER, SLOGAN } from "@/lib/data";

const PRESETS = [25, 50, 100, 250, 500, 1200];
const EMPTY = { first_name: "", last_name: "", email: "", address: "", city: "", state: "DE", zip_code: "", occupation: "", employer: "", company: "" };

const OnlinePanel = () => {
  const [amount, setAmount] = useState(100);
  const [custom, setCustom] = useState("");
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const finalAmount = custom !== "" ? Number(custom) : amount;

  const submit = async (e) => {
    e.preventDefault();
    if (!finalAmount || finalAmount <= 0) {
      toast.error("Please choose or enter a contribution amount.");
      return;
    }
    if (finalAmount > 1200) {
      toast.error("The maximum allowable contribution is $1,200 per person.");
      return;
    }
    setBusy(true);
    try {
      const res = await postForm("/payments/checkout", {
        ...form,
        amount: finalAmount,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't start secure checkout. Please try again.");
      setBusy(false);
    }
  };

  const field = (key, placeholder, opts = {}) => (
    <input
      type={opts.type || "text"}
      required={opts.required !== false}
      placeholder={placeholder}
      value={form[key]}
      onChange={(e) => setForm({ ...form, [key]: e.target.value })}
      data-testid={`donor-${key.replace(/_/g, "-")}-input`}
      className="w-full border border-navy/20 rounded-lg px-4 py-3.5 text-base placeholder:text-navy/40 focus:outline-none focus:ring-2 focus:ring-crimson focus:border-transparent"
    />
  );

  return (
    <form onSubmit={submit} data-testid="contribute-online-form">
      <p className="font-mono text-xs tracking-[0.3em] uppercase text-crimson mb-4">Choose an amount</p>
      <div className="grid grid-cols-3 gap-3 mb-4">
        {PRESETS.map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => { setAmount(v); setCustom(""); }}
            data-testid={`contribute-preset-${v}-button`}
            aria-pressed={custom === "" && amount === v}
            className={`rounded-lg border-2 py-4 font-display font-extrabold text-xl sm:text-2xl transition-all duration-150 min-h-[56px] ${
              custom === "" && amount === v
                ? "border-crimson bg-crimson text-white shadow-md"
                : "border-navy/20 text-navy hover:border-crimson/60"
            }`}
          >
            ${v.toLocaleString()}
          </button>
        ))}
      </div>
      <input
        type="number"
        min="1"
        max="1200"
        placeholder="Custom amount ($)"
        value={custom}
        onChange={(e) => setCustom(e.target.value)}
        data-testid="contribute-custom-amount-input"
        className="w-full border border-navy/20 rounded-lg px-4 py-3.5 text-base placeholder:text-navy/40 focus:outline-none focus:ring-2 focus:ring-crimson focus:border-transparent mb-8"
      />

      <p className="font-mono text-xs tracking-[0.3em] uppercase text-crimson mb-4">Your information</p>
      <input type="text" name="company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
      <div className="grid sm:grid-cols-2 gap-4 mb-4">
        {field("first_name", "First name")}
        {field("last_name", "Last name")}
        {field("email", "Email address", { type: "email" })}
        {field("zip_code", "ZIP code")}
      </div>
      <div className="space-y-4 mb-4">
        {field("address", "Street address")}
        <div className="grid grid-cols-2 gap-4">
          {field("city", "City")}
          {field("state", "State")}
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        {field("occupation", "Occupation", { required: false })}
        {field("employer", "Employer", { required: false })}
      </div>

      <button
        type="submit"
        disabled={busy}
        data-testid="contribute-online-submit-button"
        className="w-full bg-crimson hover:bg-crimson-dark disabled:opacity-60 text-white font-display font-extrabold uppercase tracking-wider text-xl sm:text-2xl px-8 py-5 rounded-full transition-all duration-200 hover:scale-[1.02] shadow-lg shadow-crimson/25 min-h-[60px]"
      >
        {busy ? "Redirecting to Secure Checkout…" : `Contribute $${(finalAmount || 0).toLocaleString()}`}
      </button>
      <p className="flex items-start gap-2 text-navy/55 text-xs sm:text-sm leading-relaxed mt-5">
        <Lock className="w-4 h-4 mt-0.5 shrink-0 text-crimson" />
        You'll be taken to Stripe's secure checkout to complete your contribution. This site never sees or stores your card number.
      </p>
    </form>
  );
};

const CheckPanel = () => {
  const copy = async () => {
    const text = `${CHECK_INFO.payableTo}\n${CHECK_INFO.poBox}\n${CHECK_INFO.cityStateZip}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Mailing address copied to clipboard.");
    } catch {
      toast.error("Copy failed — please write down the address shown.");
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-8 items-center" data-testid="contribute-check-panel">
      <div>
        <p className="font-mono text-xs tracking-[0.3em] uppercase text-crimson mb-4">Mail your contribution</p>
        <h3 className="font-display font-extrabold uppercase tracking-tight text-3xl sm:text-4xl text-navy leading-tight mb-6">
          Contribute by Check
        </h3>
        <p className="text-navy/75 text-base sm:text-lg leading-relaxed mb-6">
          Make checks payable to <strong>{CHECK_INFO.payableTo}</strong> and mail to:
        </p>
        <div className="bg-navy text-cream rounded-xl px-6 py-6 mb-5 shadow-lg" data-testid="check-mailing-address-detail">
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-gold mb-3">Checks payable & mail to</p>
          <p className="font-display font-extrabold uppercase tracking-wide text-2xl sm:text-3xl leading-tight">{CHECK_INFO.payableTo}</p>
          <p className="text-cream/85 text-lg sm:text-xl mt-2 font-semibold">{CHECK_INFO.poBox}</p>
          <p className="text-cream/85 text-lg sm:text-xl font-semibold">{CHECK_INFO.cityStateZip}</p>
        </div>
        <button
          type="button"
          onClick={copy}
          data-testid="copy-check-address-button"
          className="inline-flex items-center gap-2 border-2 border-navy hover:bg-navy hover:text-cream text-navy font-display font-extrabold uppercase tracking-wider text-lg px-7 py-3.5 rounded-full transition-colors duration-200 min-h-[52px]"
        >
          <Copy className="w-5 h-5" /> Copy Address
        </button>
        <p className="text-navy/60 text-sm sm:text-base leading-relaxed mt-6 max-w-md">
          {CHECK_INFO.maxNote}
        </p>
      </div>
      <Reveal delay={0.1}>
        <div className="relative w-full max-w-md mx-auto bg-navy rounded-xl shadow-2xl px-8 py-12 text-center overflow-hidden" data-testid="check-brand-poster">
          <StripeBar className="absolute top-0 inset-x-0 h-2.5" />
          <Star className="w-6 h-6 text-gold fill-gold mx-auto mb-6 mt-2" />
          <p className="font-display font-black uppercase text-cream leading-[0.9] text-4xl sm:text-5xl">
            Dave<br />Graham
          </p>
          <StripeBar className="h-1 w-28 mx-auto my-5 rounded-full overflow-hidden" />
          <p className="font-display font-extrabold uppercase tracking-[0.2em] text-gold text-base sm:text-lg">
            For Attorney General
          </p>
          <p className="font-editorial font-semibold text-cream/80 text-xs sm:text-sm mt-6 tracking-wide">
            {SLOGAN}
          </p>
          <p className="font-mono text-[10px] text-cream/50 uppercase tracking-[0.25em] mt-7">
            {DISCLAIMER}
          </p>
          <StripeBar className="absolute bottom-0 inset-x-0 h-2.5" />
        </div>
      </Reveal>
    </div>
  );
};

const Contribute = () => {
  const [tab, setTab] = useState("online");

  return (
    <main data-testid="contribute-page">
      <PageHero
        kicker="Support the Campaign"
        lines={["Contribute to", "the Campaign"]}
        intro="Your contribution helps Dave reach voters across Delaware and share his message. Give online or mail a check — whichever works for you."
      />

      <section className="bg-cream py-14 sm:py-20 md:py-24" data-testid="contribute-options-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="flex rounded-full bg-navy/5 border border-navy/10 p-1.5 mb-10" data-testid="contribute-tabs">
              {[
                { id: "online", label: "Give Online", icon: ShieldCheck },
                { id: "check", label: "By Check", icon: MailOpen },
              ].map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  data-testid={`contribute-option-${t.id}-tab`}
                  className={`relative flex-1 flex items-center justify-center gap-2 rounded-full py-4 font-display font-extrabold uppercase tracking-wider text-lg sm:text-xl transition-colors duration-200 min-h-[56px] ${
                    tab === t.id ? "text-white" : "text-navy/60 hover:text-navy"
                  }`}
                >
                  {tab === t.id && (
                    <motion.span layoutId="contribute-tab-pill" className="absolute inset-0 bg-crimson rounded-full shadow-md" transition={{ type: "spring", stiffness: 400, damping: 32 }} />
                  )}
                  <t.icon className="relative z-10 w-5 h-5" />
                  <span className="relative z-10">{t.label}</span>
                </button>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="bg-white rounded-2xl border border-navy/10 shadow-lg p-6 sm:p-10">
              {tab === "online" ? <OnlinePanel /> : <CheckPanel />}
            </div>
          </Reveal>

          <Reveal delay={0.15} className="mt-10">
            <div className="rounded-xl bg-navy/5 border border-navy/10 p-6 sm:p-8" data-testid="contribute-compliance-note">
              <p className="flex items-center gap-2 font-mono text-xs tracking-[0.3em] uppercase text-crimson mb-4">
                <Landmark className="w-4 h-4" /> Campaign finance compliance
              </p>
              <div className="space-y-3 text-navy/65 text-xs sm:text-sm leading-relaxed">
                <p>Contributions to Graham of Delaware, LLC are not deductible as charitable contributions for federal income tax purposes.</p>
                <p>Delaware law requires the campaign to collect and report the name, address, occupation, and employer of contributors. The maximum allowable contribution is $1,200 per person.</p>
                <p className="flex items-center gap-2 font-semibold text-navy">
                  <Star className="w-3.5 h-3.5 text-crimson fill-crimson" /> {DISCLAIMER}
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default Contribute;
